import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Mail, Phone, MapPin, Send } from 'lucide-react'

const Contact = () => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    
    try {
      const leads = JSON.parse(localStorage.getItem('contactLeads') || '[]')
      const newLead = {
        ...data,
        id: Date.now(),
        timestamp: new Date().toISOString(),
        status: 'new'
      }
      leads.push(newLead)
      localStorage.setItem('contactLeads', JSON.stringify(leads))
      
      toast.success('Message sent successfully! We\'ll get back to you soon.')
      reset()
    } catch (error) {
      toast.error('Failed to send message. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen pt-20" data-component-path="client/src/pages/Contact.jsx" data-component-line="1:1" data-component-id="client/src/pages/Contact.jsx:1:1">
      <section 
        className="relative min-h-[50vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-component-path="client/src/pages/Contact.jsx"
        data-component-line="2:3"
        data-component-id="client/src/pages/Contact.jsx:2:3"
      >
        <div className="absolute inset-0 bg-dark-primary/80" data-component-path="client/src/pages/Contact.jsx" data-component-line="10:5" data-component-id="client/src/pages/Contact.jsx:10:5"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-component-path="client/src/pages/Contact.jsx" data-component-line="12:5" data-component-id="client/src/pages/Contact.jsx:12:5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            data-component-path="client/src/pages/Contact.jsx"
            data-component-line="13:7"
            data-component-id="client/src/pages/Contact.jsx:13:7"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-component-path="client/src/pages/Contact.jsx" data-component-line="18:9" data-component-id="client/src/pages/Contact.jsx:18:9">
              Get In <span className="text-gradient" data-component-path="client/src/pages/Contact.jsx" data-component-line="19:23" data-component-id="client/src/pages/Contact.jsx:19:23">Touch</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-component-path="client/src/pages/Contact.jsx" data-component-line="21:9" data-component-id="client/src/pages/Contact.jsx:21:9">
              Let's discuss how we can transform your business with our enterprise solutions
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-dark-primary" data-component-path="client/src/pages/Contact.jsx" data-component-line="28:3" data-component-id="client/src/pages/Contact.jsx:28:3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="client/src/pages/Contact.jsx" data-component-line="29:5" data-component-id="client/src/pages/Contact.jsx:29:5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-component-path="client/src/pages/Contact.jsx" data-component-line="30:7" data-component-id="client/src/pages/Contact.jsx:30:7">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              data-component-path="client/src/pages/Contact.jsx"
              data-component-line="31:9"
              data-component-id="client/src/pages/Contact.jsx:31:9"
            >
              <h2 className="text-3xl font-bold mb-6" data-component-path="client/src/pages/Contact.jsx" data-component-line="36:11" data-component-id="client/src/pages/Contact.jsx:36:11">Contact Information</h2>
              <p className="text-gray-400 mb-8" data-component-path="client/src/pages/Contact.jsx" data-component-line="37:11" data-component-id="client/src/pages/Contact.jsx:37:11">
                Reach out to us through any of these channels. Our team is ready to help you succeed.
              </p>

              <div className="flex flex-col gap-6" data-component-path="client/src/pages/Contact.jsx" data-component-line="41:11" data-component-id="client/src/pages/Contact.jsx:41:11">
                <div className="flex items-start gap-4" data-component-path="client/src/pages/Contact.jsx" data-component-line="42:13" data-component-id="client/src/pages/Contact.jsx:42:13">
                  <div className="w-12 h-12 bg-neon-green/10 rounded-xl flex items-center justify-center flex-shrink-0" data-component-path="client/src/pages/Contact.jsx" data-component-line="43:15" data-component-id="client/src/pages/Contact.jsx:43:15">
                    <Mail className="w-6 h-6 text-neon-green" data-component-path="client/src/pages/Contact.jsx" data-component-line="44:17" data-component-id="client/src/pages/Contact.jsx:44:17" />
                  </div>
                  <div data-component-path="client/src/pages/Contact.jsx" data-component-line="46:15" data-component-id="client/src/pages/Contact.jsx:46:15">
                    <h3 className="font-semibold text-white mb-1" data-component-path="client/src/pages/Contact.jsx" data-component-line="47:17" data-component-id="client/src/pages/Contact.jsx:47:17">Email</h3>
                    <a href="mailto:info@darktechsolutions.com" className="text-gray-400 hover:text-neon-green transition-colors" data-component-path="client/src/pages/Contact.jsx" data-component-line="48:17" data-component-id="client/src/pages/Contact.jsx:48:17">
                      info@darktechsolutions.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-component-path="client/src/pages/Contact.jsx" data-component-line="54:13" data-component-id="client/src/pages/Contact.jsx:54:13">
                  <div className="w-12 h-12 bg-neon-green/10 rounded-xl flex items-center justify-center flex-shrink-0" data-component-path="client/src/pages/Contact.jsx" data-component-line="55:15" data-component-id="client/src/pages/Contact.jsx:55:15">
                    <Phone className="w-6 h-6 text-neon-green" data-component-path="client/src/pages/Contact.jsx" data-component-line="56:17" data-component-id="client/src/pages/Contact.jsx:56:17" />
                  </div>
                  <div data-component-path="client/src/pages/Contact.jsx" data-component-line="58:15" data-component-id="client/src/pages/Contact.jsx:58:15">
                    <h3 className="font-semibold text-white mb-1" data-component-path="client/src/pages/Contact.jsx" data-component-line="59:17" data-component-id="client/src/pages/Contact.jsx:59:17">Phone</h3>
                    <a href="tel:+1234567890" className="text-gray-400 hover:text-neon-green transition-colors" data-component-path="client/src/pages/Contact.jsx" data-component-line="60:17" data-component-id="client/src/pages/Contact.jsx:60:17">
                      +1 (234) 567-890
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-component-path="client/src/pages/Contact.jsx" data-component-line="66:13" data-component-id="client/src/pages/Contact.jsx:66:13">
                  <div className="w-12 h-12 bg-neon-green/10 rounded-xl flex items-center justify-center flex-shrink-0" data-component-path="client/src/pages/Contact.jsx" data-component-line="67:15" data-component-id="client/src/pages/Contact.jsx:67:15">
                    <MapPin className="w-6 h-6 text-neon-green" data-component-path="client/src/pages/Contact.jsx" data-component-line="68:17" data-component-id="client/src/pages/Contact.jsx:68:17" />
                  </div>
                  <div data-component-path="client/src/pages/Contact.jsx" data-component-line="70:15" data-component-id="client/src/pages/Contact.jsx:70:15">
                    <h3 className="font-semibold text-white mb-1" data-component-path="client/src/pages/Contact.jsx" data-component-line="71:17" data-component-id="client/src/pages/Contact.jsx:71:17">Address</h3>
                    <p className="text-gray-400" data-component-path="client/src/pages/Contact.jsx" data-component-line="72:17" data-component-id="client/src/pages/Contact.jsx:72:17">
                      123 Tech Street<br data-component-path="client/src/pages/Contact.jsx" data-component-line="73:38" data-component-id="client/src/pages/Contact.jsx:73:38" />
                      Innovation City, TC 12345
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="glass-panel rounded-2xl p-8"
              data-component-path="client/src/pages/Contact.jsx"
              data-component-line="82:9"
              data-component-id="client/src/pages/Contact.jsx:82:9"
            >
              <h2 className="text-3xl font-bold mb-6" data-component-path="client/src/pages/Contact.jsx" data-component-line="88:11" data-component-id="client/src/pages/Contact.jsx:88:11">Send Us a Message</h2>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-component-path="client/src/pages/Contact.jsx" data-component-line="89:11" data-component-id="client/src/pages/Contact.jsx:89:11">
                <div data-component-path="client/src/pages/Contact.jsx" data-component-line="90:13" data-component-id="client/src/pages/Contact.jsx:90:13">
                  <label className="block text-sm font-semibold mb-2" data-component-path="client/src/pages/Contact.jsx" data-component-line="91:15" data-component-id="client/src/pages/Contact.jsx:91:15">Full Name *</label>
                  <input
                    type="text"
                    {...register('name', { required: 'Name is required' })}
                    className="w-full px-4 py-3 bg-dark-secondary border border-neon-green/20 rounded-xl text-white focus:border-neon-green focus:outline-none transition-colors"
                    placeholder="John Doe"
                    data-component-path="client/src/pages/Contact.jsx"
                    data-component-line="92:15"
                    data-component-id="client/src/pages/Contact.jsx:92:15"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1" data-component-path="client/src/pages/Contact.jsx" data-component-line="99:35" data-component-id="client/src/pages/Contact.jsx:99:35">{errors.name.message}</p>}
                </div>

                <div data-component-path="client/src/pages/Contact.jsx" data-component-line="102:13" data-component-id="client/src/pages/Contact.jsx:102:13">
                  <label className="block text-sm font-semibold mb-2" data-component-path="client/src/pages/Contact.jsx" data-component-line="103:15" data-component-id="client/src/pages/Contact.jsx:103:15">Email *</label>
                  <input
                    type="email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="w-full px-4 py-3 bg-dark-secondary border border-neon-green/20 rounded-xl text-white focus:border-neon-green focus:outline-none transition-colors"
                    placeholder="john@example.com"
                    data-component-path="client/src/pages/Contact.jsx"
                    data-component-line="104:15"
                    data-component-id="client/src/pages/Contact.jsx:104:15"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1" data-component-path="client/src/pages/Contact.jsx" data-component-line="117:36" data-component-id="client/src/pages/Contact.jsx:117:36">{errors.email.message}</p>}
                </div>

                <div data-component-path="client/src/pages/Contact.jsx" data-component-line="120:13" data-component-id="client/src/pages/Contact.jsx:120:13">
                  <label className="block text-sm font-semibold mb-2" data-component-path="client/src/pages/Contact.jsx" data-component-line="121:15" data-component-id="client/src/pages/Contact.jsx:121:15">Company</label>
                  <input
                    type="text"
                    {...register('company')}
                    className="w-full px-4 py-3 bg-dark-secondary border border-neon-green/20 rounded-xl text-white focus:border-neon-green focus:outline-none transition-colors"
                    placeholder="Your Company"
                    data-component-path="client/src/pages/Contact.jsx"
                    data-component-line="122:15"
                    data-component-id="client/src/pages/Contact.jsx:122:15"
                  />
                </div>

                <div data-component-path="client/src/pages/Contact.jsx" data-component-line="131:13" data-component-id="client/src/pages/Contact.jsx:131:13">
                  <label className="block text-sm font-semibold mb-2" data-component-path="client/src/pages/Contact.jsx" data-component-line="132:15" data-component-id="client/src/pages/Contact.jsx:132:15">Service Interest</label>
                  <select
                    {...register('service')}
                    className="w-full px-4 py-3 bg-dark-secondary border border-neon-green/20 rounded-xl text-white focus:border-neon-green focus:outline-none transition-colors"
                    data-component-path="client/src/pages/Contact.jsx"
                    data-component-line="133:15"
                    data-component-id="client/src/pages/Contact.jsx:133:15"
                  >
                    <option value="" data-component-path="client/src/pages/Contact.jsx" data-component-line="138:17" data-component-id="client/src/pages/Contact.jsx:138:17">Select a service</option>
                    <option value="web-development" data-component-path="client/src/pages/Contact.jsx" data-component-line="139:17" data-component-id="client/src/pages/Contact.jsx:139:17">Web Development</option>
                    <option value="saas-development" data-component-path="client/src/pages/Contact.jsx" data-component-line="140:17" data-component-id="client/src/pages/Contact.jsx:140:17">SaaS Development</option>
                    <option value="erp-solutions" data-component-path="client/src/pages/Contact.jsx" data-component-line="141:17" data-component-id="client/src/pages/Contact.jsx:141:17">ERP Solutions</option>
                    <option value="odoo-implementation" data-component-path="client/src/pages/Contact.jsx" data-component-line="142:17" data-component-id="client/src/pages/Contact.jsx:142:17">Odoo Implementation</option>
                    <option value="ai-automation" data-component-path="client/src/pages/Contact.jsx" data-component-line="143:17" data-component-id="client/src/pages/Contact.jsx:143:17">AI & Automation</option>
                  </select>
                </div>

                <div data-component-path="client/src/pages/Contact.jsx" data-component-line="147:13" data-component-id="client/src/pages/Contact.jsx:147:13">
                  <label className="block text-sm font-semibold mb-2" data-component-path="client/src/pages/Contact.jsx" data-component-line="148:15" data-component-id="client/src/pages/Contact.jsx:148:15">Message *</label>
                  <textarea
                    {...register('message', { required: 'Message is required' })}
                    rows="5"
                    className="w-full px-4 py-3 bg-dark-secondary border border-neon-green/20 rounded-xl text-white focus:border-neon-green focus:outline-none transition-colors resize-none"
                    placeholder="Tell us about your project..."
                    data-component-path="client/src/pages/Contact.jsx"
                    data-component-line="149:15"
                    data-component-id="client/src/pages/Contact.jsx:149:15"
                  ></textarea>
                  {errors.message && <p className="text-red-500 text-sm mt-1" data-component-path="client/src/pages/Contact.jsx" data-component-line="156:38" data-component-id="client/src/pages/Contact.jsx:156:38">{errors.message.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 px-8 bg-neon-green text-dark-primary font-bold rounded-xl hover:shadow-lg hover:shadow-neon-green/50 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-component-path="client/src/pages/Contact.jsx"
                  data-component-line="159:13"
                  data-component-id="client/src/pages/Contact.jsx:159:13"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'} <Send className="w-5 h-5" data-component-path="client/src/pages/Contact.jsx" data-component-line="166:62" data-component-id="client/src/pages/Contact.jsx:166:62" />
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact